<template>
    <div class="make-model-bot">
        <car-loader-component :show="loadingRangeId || !currentRange" :loadingText="loadingText"></car-loader-component>
        <div v-show="!(loadingRangeId || !currentRange)" class="make-model-bot__container">
            <div class="make-model-bot__body">
                <chat-container :conversationId="conversationId" :chatMessageQueue="chatMessageQueue" 
                @onMakeModelAnswerSelected="onQuestionAnsweredCallback($event)"
                @onMakeModelHelpSelected="helpMeCallBack()"
                @onChatFinished="onChatFinished()"
                @onQueueStatusChanged="onQueueStatusChanged($event)" ></chat-container>

                <div v-if="showSubmit"><submit-answers/></div>
            </div>
        </div>
        <div id="make-model-chat-messages"></div>
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import ChatMessage from '@/models/vehicle/makeModelBot/chatMessage';
import MakeModelChatType from '@/enums/MakeModelChatType';
import { makeModelBotService, MakeModelBotService } from '@/services/MakeModelBotService';
import ChatQuestion from '@/models/vehicle/makeModelBot/chatQuestion';
import KeyValue from '@/models/KeyValue';
import { trimChatbotService } from '@/services/TrimChatbotService';
import CarLoaderComponent from '@/components/ui/CarLoaderComponent.vue';
import ChatBotType from '@/enums/ChatBotType';
import MakeModelChatQueueStatus from '@/enums/MakeModelChatMessageQueueStatus';
import SubmitAnswersComponent from './SubmitAnswersComponent.vue';
import ChatContainerComponent from './ChatContainerComponent.vue';

@Component({
    components: {
        'chat-container': ChatContainerComponent,
        'submit-answers': SubmitAnswersComponent,
        'car-loader': CarLoaderComponent,
        CarLoaderComponent
    }
})

export default class MakeModelBotComponent extends Vue {
    public visible: boolean = false;

    @Prop({ default: 'Car Configurator' })
    public title!: string;

    @Prop({ default: 'Which body style do you prefer?' })
    public initialMessage!: string;

    @Prop({ default: false })
    public hideInsights!: boolean;

    private chatMessageQueue: ChatMessage[] = [];

    private conversationId: string = '';

    public get currentQuestion(): ChatQuestion {
        return this.ChatQuestions[this.questionindex];
    }

    public ChatQuestions: ChatQuestion[] = [];

    private questionindex: number = 0;

    private canShowSubmit: boolean = false;
    
    private showSubmit: boolean = false;

    private questionAnswers: KeyValue[] = [];

    private loadingRangeId: number | null = null;

    private loadingText: string = 'The Askaprice assistant is getting ready to help you pick your car...';

    private async setUpBot() {
        // resetting the form triggers this watch a few times,
        // so ensure we're not trying to fetch null, and ensure
        // we're only fetching the actual range once
        if (!this.currentRange) {
            return;
        }

        if (this.loadingRangeId && this.loadingRangeId === this.currentRange.rangeId) {
            return;
        }

        this.conversationId = crypto.randomUUID();
        this.loadingRangeId = this.currentRange.rangeId;
        this.chatMessageQueue = [];
        this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.BotMessage, {
            text: 'Hello! I\'m your Askaprice assistant, here to guide you in configuring your ideal car'
        }));
        this.questionindex = 0;
        this.showSubmit = false;
        this.canShowSubmit = false;
        this.ChatQuestions = await MakeModelBotService.instance.buildChatQuestions(
            this.currentRange.manufacturerId,
            this.currentRange.rangeId
        );

        this.setQuestion();

        this.loadingRangeId = null;

        // set watch call backs for answers and when the answers are ready call the callbacks
        // this allows us to just use config for all of the questions and not have logic in the chat component
        this.ChatQuestions.forEach((ChatQuestion) => {
            this.$watch(
                () => ChatQuestion.answers(),
                (newVal, oldVal) => {
                    if (!newVal || newVal.length === 0) {
                        return;
                    }
                    if (!oldVal || this.notEqual(newVal, oldVal)) {
                        if (newVal.length === 1) {
                            ChatQuestion.onAnswer(newVal[0]);
                            ChatQuestion.canSkipQuestion = true;
                            ChatQuestion.isReady = true;
                            return;
                        }
                        ChatQuestion.answersReadyCallback(newVal, oldVal, this.chatMessageQueue);
                        ChatQuestion.isReady = true;
                    }
                },
                { immediate: true }
            );
        });
    }

    mounted() {
        this.$watch(
            () => this.currentRange,
            (newVal, oldVal) => this.setUpBot()
        );
    }

    public async helpMeCallBack() {
        const reply = this.currentQuestion.onHelp();
        this.chatMessageQueue.push(reply);

        this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.Answers, {
            makeModelChatQuestionType: this.currentQuestion.makeModelChatQuestionType,
            text: this.currentQuestion.question,
            choices: this.currentQuestion.answers(),
            bypassConfirmation: this.currentQuestion.bypassConfirmation,
            helpOptions:
            {
                helpButton: true,
                helpRequested: true
            }
        }));

        if (this.questionAnswers.length > 0) {
            trimChatbotService.sendChatHistory(this.questionAnswers, this.currentRange.rangeId, this.getCorrelationId(), this.conversationId, ChatBotType.GeneralBot);
        }
    }

    private returnValidAnswer(answer: string): string {
        if (answer === 'notsure') {
            return 'Not sure';
        }
        return answer.charAt(0).toUpperCase() + answer.slice(1);
    }

    public onQuestionAnsweredCallback(answer: string) {
        const messages = this.currentQuestion.onAnswer(answer);
        this.questionAnswers.push({ key: this.currentQuestion.question, value: answer });
        this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.UserMessage, {
            text: this.returnValidAnswer(answer)
        }));
        messages.forEach((message) => {
            this.chatMessageQueue.push(message);
        });

        this.questionindex += 1;
        this.setQuestion();
    }

    public onQueueStatusChanged(status: MakeModelChatQueueStatus) {
        if (status === MakeModelChatQueueStatus.Finished && this.canShowSubmit) {
            this.showSubmit = true;
        }
    }

    public onChatFinished() {
        this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.Answers, {
            makeModelChatQuestionType: this.currentQuestion.makeModelChatQuestionType,
            text: this.currentQuestion.question,
            choices: this.currentQuestion.answers(),
            bypassConfirmation: this.currentQuestion.bypassConfirmation,
            helpOptions:
            {
                helpButton: true,
                helpRequested: true
            }
        }));
    }

    private getCorrelationId(): string {
        return this.$store.getters['initialSettingsModule/correlationId'];
    }

    public get currentRange() {
        return this.$store.getters['vehicleModule/currentRange'];
    }

    private submitChoice(): void {
        const derivatives = this.$store.getters['vehicleSearchModule/derivatives'];
        this.$store.dispatch('vehicleModule/submitDerivative', derivatives[0]);
    }

    private async setQuestion() {
        if (this.questionindex === this.ChatQuestions.length) {
            this.submitChoice();
            this.canShowSubmit = true;
            return;
        }

        if (await this.ChatQuestions[this.questionindex].skipQuestion()) {
            this.questionindex += 1;
            this.setQuestion();
            return;
        }

        this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.BotMessage, {
            text: this.currentQuestion.question
        }));

        if (!this.hideInsights) {
            this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.BotMessage, {
                text: this.currentQuestion.questionInsight
            }));
        }

        this.chatMessageQueue.push(new ChatMessage(MakeModelChatType.Answers, {
            makeModelChatQuestionType: this.currentQuestion?.makeModelChatQuestionType,
            bypassConfirmation: this.currentQuestion.bypassConfirmation,
            choices: this.currentQuestion.answers(),
            helpOptions:
            {
                helpButton: this.currentQuestion.showHelp,
                helpRequested: false
            }
        }));
    }
    
    private notEqual(a: string[], b: string[]): boolean {
        return a.slice().sort().join('-') !== b.slice().sort().join('-');
    }    
}
</script>
